const PENDING_USER_INVITE_TOKEN_KEY = "trekkin.pendingUserInviteToken";
const PENDING_EVENT_INVITE_KEY = "trekkin.pendingEventInvite";
const CLAIMED_EVENT_INVITE_KEY = "trekkin.claimedEventInvite";
const API_BASE_FALLBACK = "https://trekkin-api-snowy-sunset-6669.fly.dev";

export function getApiBase() {
  return import.meta?.env?.VITE_API_BASE || API_BASE_FALLBACK;
}

export async function fetchReferralApi(path, { token, method = "POST", body } = {}) {
  const res = await fetch(`${getApiBase()}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  let data = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }

  if (!res.ok || data?.ok === false) {
    throw new Error(String(data?.error || `request_failed_${res.status}`));
  }

  return data || { ok: true };
}

function safeJsonParse(raw, fallback = null) {
  try {
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

export function storePendingUserInviteToken(token) {
  if (!token) return;
  try {
    localStorage.setItem(PENDING_USER_INVITE_TOKEN_KEY, String(token).trim());
  } catch {}
}

export function getPendingUserInviteToken() {
  try {
    return String(localStorage.getItem(PENDING_USER_INVITE_TOKEN_KEY) || "").trim();
  } catch {
    return "";
  }
}

export function clearPendingUserInviteToken() {
  try {
    localStorage.removeItem(PENDING_USER_INVITE_TOKEN_KEY);
  } catch {}
}

export function storePendingEventInvite(payload) {
  if (!payload?.token || !payload?.eventId) return;
  try {
    localStorage.setItem(
      PENDING_EVENT_INVITE_KEY,
      JSON.stringify({
        token: String(payload.token).trim(),
        eventId: String(payload.eventId).trim(),
        eventName: String(payload.eventName || "").trim(),
        storedAt: Date.now(),
      })
    );
  } catch {}
}

export function getPendingEventInvite() {
  try {
    return safeJsonParse(localStorage.getItem(PENDING_EVENT_INVITE_KEY), null);
  } catch {
    return null;
  }
}

export function clearPendingEventInvite() {
  try {
    localStorage.removeItem(PENDING_EVENT_INVITE_KEY);
  } catch {}
}

export function storeClaimedEventInvite(payload) {
  if (!payload?.eventId || !payload?.inviteId || !payload?.inviterUid) return;
  try {
    localStorage.setItem(
      CLAIMED_EVENT_INVITE_KEY,
      JSON.stringify({
        inviteId: String(payload.inviteId).trim(),
        eventId: String(payload.eventId).trim(),
        eventName: String(payload.eventName || "").trim(),
        inviterUid: String(payload.inviterUid).trim(),
        inviterName: String(payload.inviterName || "").trim(),
        claimedAt: Date.now(),
      })
    );
  } catch {}
}

export function getClaimedEventInvite() {
  try {
    return safeJsonParse(localStorage.getItem(CLAIMED_EVENT_INVITE_KEY), null);
  } catch {
    return null;
  }
}

export function clearClaimedEventInvite(eventId = null) {
  try {
    if (!eventId) {
      localStorage.removeItem(CLAIMED_EVENT_INVITE_KEY);
      return;
    }
    const current = getClaimedEventInvite();
    if (String(current?.eventId || "") === String(eventId)) {
      localStorage.removeItem(CLAIMED_EVENT_INVITE_KEY);
    }
  } catch {}
}

export function captureReferralTokensFromUrl() {
  if (typeof window === "undefined") return;

  try {
    const url = new URL(window.location.href);
    const userInviteToken = String(url.searchParams.get("inviteToken") || "").trim();
    const eventInviteToken = String(url.searchParams.get("eventInviteToken") || "").trim();
    const eventId = String(url.searchParams.get("eventId") || url.searchParams.get("event") || "").trim();
    const eventName = String(url.searchParams.get("eventName") || "").trim();

    if (userInviteToken) {
      storePendingUserInviteToken(userInviteToken);
      url.searchParams.delete("inviteToken");
    }

    if (eventInviteToken && eventId) {
      storePendingEventInvite({
        token: eventInviteToken,
        eventId,
        eventName,
      });
      url.searchParams.delete("eventInviteToken");
      if (eventName) url.searchParams.delete("eventName");
    }

    if (userInviteToken || eventInviteToken) {
      window.history.replaceState({}, "", url.toString());
    }
  } catch {}
}
